import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import { toast } from 'sonner';
import Header from '../components/Header';
import VideoLessonPlayer from '../components/VideoLessonPlayer';
import MathText from '../components/MathText';
import {
  ArrowLeft, Loader2, Sparkles, CheckCircle2, Circle, XCircle,
  Trophy, RotateCcw, ChevronRight, MonitorPlay, BookOpen,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CourseChapter() {
  const { courseId, chapterId } = useParams();
  const navigate = useNavigate();

  const [lesson, setLesson] = useState(null);
  const [loadingLesson, setLoadingLesson] = useState(true);
  const [phase, setPhase] = useState('lesson'); // lesson | quiz | result
  const [quiz, setQuiz] = useState(null);
  const [loadingQuiz, setLoadingQuiz] = useState(false);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);
  const [viewMode, setViewMode] = useState('read'); // read | watch
  const [video, setVideo] = useState(null);
  const [loadingVideo, setLoadingVideo] = useState(false);

  useEffect(() => {
    setLoadingLesson(true);
    setPhase('lesson'); setQuiz(null); setAnswers({}); setResult(null);
    setViewMode('read'); setVideo(null);
    axios.get(`${API_URL}/api/courses/${courseId}/chapters/${chapterId}/lesson`)
      .then(({ data }) => setLesson(data))
      .catch((e) => {
        const detail = e.response?.data?.detail;
        if (detail?.code === 'PRO_REQUIRED') { toast.error(detail.message); navigate('/upgrade'); }
        else { toast.error('Could not load lesson. Please retry.'); navigate(`/courses/${courseId}`); }
      })
      .finally(() => setLoadingLesson(false));
  }, [courseId, chapterId, navigate]);

  const switchToWatch = useCallback(async () => {
    setViewMode('watch');
    if (video || loadingVideo) return;
    setLoadingVideo(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/courses/${courseId}/chapters/${chapterId}/video`);
      setVideo(data);
    } catch {
      toast.error('Video generation failed. Please retry.');
      setViewMode('read');
    } finally {
      setLoadingVideo(false);
    }
  }, [courseId, chapterId, video, loadingVideo]);

  const startQuiz = useCallback(async () => {
    setLoadingQuiz(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/courses/${courseId}/chapters/${chapterId}/quiz`);
      setQuiz(data);
      setPhase('quiz');
    } catch {
      toast.error('Quiz generation failed. Please retry.');
    } finally {
      setLoadingQuiz(false);
    }
  }, [courseId, chapterId]);

  const submitQuiz = async () => {
    setSubmitting(true);
    try {
      const payload = { answers: Object.entries(answers).map(([q_idx, selected]) => ({ q_idx: parseInt(q_idx, 10), selected })) };
      const { data } = await axios.post(`${API_URL}/api/courses/${courseId}/chapters/${chapterId}/quiz/submit`, payload);
      setResult(data);
      setPhase('result');
      if (data.passed) toast.success(`Chapter completed! ${data.points_awarded > 0 ? `+${data.points_awarded} points 🎉` : ''}`);
      else toast.warning(`Scored ${data.percent}% — need ${data.pass_percent}% to complete. Revise and retry!`);
    } catch {
      toast.error('Failed to submit quiz');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingLesson) {
    return (
      <div className="min-h-screen"><Header />
        <div className="flex flex-col items-center justify-center py-24 gap-3">
          <Loader2 className="w-10 h-10 animate-spin text-[#0EA5E9]" />
          <p className="text-sm text-[#6B7280] flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-amber-500" /> Preparing your AI lesson…</p>
        </div>
      </div>
    );
  }
  if (!lesson) return null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="course-chapter-page">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => navigate(`/courses/${courseId}`)} className="flex items-center gap-1.5 text-sm font-bold text-[#6B7280] hover:text-[#111827] mb-5" data-testid="back-to-course-btn">
          <ArrowLeft className="w-4 h-4" /> {lesson.course_title}
        </button>

        {/* ===== Lesson phase ===== */}
        {phase === 'lesson' && (
          <>
            {/* Watch / Read toggle */}
            <div className="flex bg-white rounded-2xl border border-slate-200 p-1 mb-5 w-fit" data-testid="lesson-mode-toggle">
              <button
                onClick={() => setViewMode('read')}
                data-testid="read-mode-btn"
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition ${viewMode === 'read' ? 'bg-[#0EA5E9] text-white shadow' : 'text-[#6B7280] hover:text-[#111827]'}`}
              >
                <BookOpen className="w-4 h-4" /> Read
              </button>
              <button
                onClick={switchToWatch}
                data-testid="watch-mode-btn"
                className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold transition ${viewMode === 'watch' ? 'bg-[#0EA5E9] text-white shadow' : 'text-[#6B7280] hover:text-[#111827]'}`}
              >
                <MonitorPlay className="w-4 h-4" /> Watch
              </button>
            </div>

            {viewMode === 'watch' ? (
              <div className="mb-6" data-testid="watch-mode-container">
                {loadingVideo ? (
                  <div className="rounded-2xl bg-slate-950 aspect-video sm:aspect-[16/8] flex flex-col items-center justify-center gap-3" data-testid="video-loading">
                    <Loader2 className="w-10 h-10 animate-spin text-cyan-400" />
                    <p className="text-sm text-slate-400 flex items-center gap-1.5"><Sparkles className="w-4 h-4 text-amber-400" /> Producing your video lesson…</p>
                  </div>
                ) : video ? (
                  <>
                    <VideoLessonPlayer slides={video.slides} onFinished={() => toast.success('Lesson complete! Take the chapter quiz below 👇')} />
                    <p className="text-[11px] text-[#9CA3AF] mt-2 text-center">Narrated by your device's voice — pick a different voice or speed from the player bar.</p>
                  </>
                ) : null}
              </div>
            ) : (
              <div className="quiz-card no-lift p-6 sm:p-8 mb-6" data-testid="lesson-content">
                <p className="text-xs font-bold uppercase tracking-widest text-[#0EA5E9] mb-1 flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5" /> AI Lesson
                </p>
                <h1 className="text-xl sm:text-2xl font-black text-[#111827] mb-5" style={{ fontFamily: 'Fredoka, sans-serif' }}>{lesson.chapter_title}</h1>
                {lesson.diagram && (
                  <img
                    src={`data:image/png;base64,${lesson.diagram}`}
                    alt={`${lesson.chapter_title} diagram`}
                    className="w-full max-w-xl mx-auto rounded-2xl border border-slate-200 mb-6"
                    data-testid="lesson-diagram"
                  />
                )}
                <div className="prose prose-sm prose-slate max-w-none prose-headings:font-black prose-headings:text-[#111827] prose-li:my-0.5">
                  <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[[rehypeKatex, { throwOnError: false, strict: false }]]}>{lesson.content}</ReactMarkdown>
                </div>
              </div>
            )}
            <button
              onClick={startQuiz}
              disabled={loadingQuiz}
              data-testid="start-chapter-quiz-btn"
              className="w-full px-5 py-3.5 rounded-2xl font-bold text-base bg-[#0EA5E9] text-white shadow-[0_4px_0_rgb(2,132,199)] active:translate-y-1 active:shadow-none hover:bg-[#0284C7] flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loadingQuiz ? <><Loader2 className="w-5 h-5 animate-spin" /> Generating quiz…</> : <>Take Chapter Quiz <ChevronRight className="w-5 h-5" /></>}
            </button>
          </>
        )}

        {/* ===== Quiz phase ===== */}
        {phase === 'quiz' && quiz && (
          <div data-testid="chapter-quiz">
            <div className="quiz-card p-5 mb-5 flex items-center justify-between">
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-[#6B7280]">Chapter Quiz</p>
                <p className="text-lg font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }}>
                  {Object.keys(answers).length} / {quiz.questions.length} answered
                </p>
              </div>
              <span className="px-3 py-1.5 rounded-xl bg-amber-100 text-amber-700 text-xs font-bold">Score {quiz.pass_percent}%+ to complete</span>
            </div>

            <div className="space-y-5">
              {quiz.questions.map((q, idx) => (
                <div key={idx} className="quiz-card p-5" data-testid={`quiz-question-${idx}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-bold text-[#6B7280]">Q{idx + 1}{q.topic ? ` · ${q.topic}` : ''}</span>
                  </div>
                  <p className="text-sm text-[#111827] mb-4 whitespace-pre-line"><MathText>{q.question}</MathText></p>
                  {q.image && (
                    <img src={q.image} alt="Chemical structure" className="max-w-[340px] w-full mx-auto rounded-xl border border-slate-200 bg-white mb-4" data-testid={`q${idx}-structure-img`} />
                  )}
                  <div className="space-y-2">
                    {q.options.map((opt) => {
                      const letter = (opt.match(/^([A-D])[\.\)]/) || [])[1] || opt.charAt(0).toUpperCase();
                      const isSelected = answers[idx] === letter;
                      return (
                        <button
                          key={opt}
                          onClick={() => setAnswers({ ...answers, [idx]: letter })}
                          data-testid={`q${idx}-option-${letter}`}
                          className={`w-full text-left px-4 py-2.5 rounded-xl border-2 transition-all flex items-start gap-2.5 ${
                            isSelected ? 'border-[#0EA5E9] bg-cyan-50' : 'border-slate-200 hover:border-slate-300 bg-white'
                          }`}
                        >
                          {isSelected ? <CheckCircle2 className="w-4 h-4 text-[#0EA5E9] shrink-0 mt-0.5" /> : <Circle className="w-4 h-4 text-slate-300 shrink-0 mt-0.5" />}
                          <span className="text-sm text-[#111827]"><MathText>{opt}</MathText></span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={submitQuiz}
              disabled={submitting || Object.keys(answers).length === 0}
              data-testid="submit-chapter-quiz-btn"
              className="w-full mt-6 px-5 py-3.5 rounded-2xl font-bold text-base bg-emerald-600 text-white hover:bg-emerald-700 flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting…</> : 'Submit Quiz'}
            </button>
          </div>
        )}

        {/* ===== Result phase ===== */}
        {phase === 'result' && result && (
          <div data-testid="chapter-quiz-result">
            <div className="quiz-card p-8 text-center mb-6">
              <div className={`inline-flex items-center justify-center w-16 h-16 rounded-3xl shadow-lg mb-4 ${result.passed ? 'bg-gradient-to-br from-emerald-400 to-teal-500' : 'bg-gradient-to-br from-amber-400 to-orange-500'}`}>
                {result.passed ? <Trophy className="w-8 h-8 text-white" /> : <RotateCcw className="w-8 h-8 text-white" />}
              </div>
              <h2 className="text-3xl font-black text-[#111827]" style={{ fontFamily: 'Fredoka, sans-serif' }} data-testid="chapter-quiz-score">
                {result.correct} <span className="text-[#9CA3AF] text-xl">/ {result.total}</span>
              </h2>
              <p className="text-sm text-[#6B7280] mt-1">
                {result.percent}% · {result.passed ? 'Chapter completed! 🎉' : `Need ${result.pass_percent}% to complete — revise and retry`}
                {result.points_awarded > 0 && <span className="font-bold text-emerald-600"> · +{result.points_awarded} points</span>}
              </p>
            </div>

            <div className="space-y-3 mb-6">
              {result.per_question.map((r) => (
                <div key={r.q_idx} className={`quiz-card px-4 py-3 flex items-start gap-3 ${r.is_correct === false ? 'border-rose-200' : ''}`}>
                  {r.is_correct ? <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" /> : <XCircle className="w-4 h-4 text-rose-400 shrink-0 mt-0.5" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-xs text-[#6B7280]">
                      Q{r.q_idx + 1} · You: <b>{r.selected || '—'}</b> · Correct: <b className="text-emerald-600">{r.correct_answer}</b>
                    </p>
                    {r.explanation && <p className="text-xs text-[#374151] mt-1"><MathText>{r.explanation}</MathText></p>}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex gap-3">
              {!result.passed && (
                <button onClick={() => { setPhase('quiz'); setAnswers({}); setResult(null); }} data-testid="retry-chapter-quiz-btn"
                  className="flex-1 px-5 py-3 rounded-2xl font-bold text-sm bg-amber-500 text-white hover:bg-amber-600">
                  Retry Quiz
                </button>
              )}
              <button onClick={() => navigate(`/courses/${courseId}`)} data-testid="back-to-chapters-btn"
                className="flex-1 px-5 py-3 rounded-2xl font-bold text-sm bg-[#0EA5E9] text-white hover:bg-[#0284C7]">
                {result.passed ? 'Next Chapter →' : 'Back to Chapters'}
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
